import { hasPermission } from '../../permissions/usePermissions';
import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import {
  Modal,
  Card,
  message,
  notification,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Table,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Select,
  Descriptions,
  Drawer,
  DatePicker,
  Popover
} from "antd";

import {
  useFetchBonlivById,
  useFetchItemsInBonliv,
  useValidateBonliv,
  useUpdateBonlivStatus,
  useSaveBonliv,
  useUnvalidateBonliv,
  useTransferBonlivToGroupe
} from "../../hooks/bonlivHooks";
import BonlivAddItemForm from "./AddItemToBonlivForm";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  EyeOutlined,
  PrinterOutlined,
  CheckOutlined,
  UndoOutlined,
  CalendarOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  CloseOutlined,
  MoreOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { usePaymentModes, useDeliveryModes } from '../../hooks/listsHooks';
import ComList from '../com/ComList';
import { useFetchComById } from "../../hooks/comHooks";
import { useUsers } from "../../hooks/userHooks";
import { useFetchAllEntrepots } from "../../hooks/entrepotHooks";
import NestedBonlivTable from './NestedBonlivTable';
import { printBonliv } from './BonlivPrintUtility';
import PrintGroupe from './PrintGroupe';
import PrintRecuGratuit from './PrintRecuGratuit';
import EnhancedEditableField from '../../utils/EnhancedEditableField';
import ChangesSummaryModal from '../../utils/ChangesSummaryModal';
import { motion } from 'framer-motion';
import bonlivLockService from '../../services/bonlivLockService';
import moment from 'moment';
import { getClientById, getAllClients } from '../../models/clientAPI';

const { Title, Text } = Typography;
const { Option } = Select;

const titleStyle = {
  marginBottom: "4px",
  marginTop: "0px",
};

const formatDate = (date) => {
  if (!date) return "Non fourni";
  
  const dateObj = new Date(date);
  
  // Format date in European style (dd/MM/yyyy)
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

const formatDateTime = (date) => {
  if (!date) return "Non fourni";
  
  // If it's an ISO string, extract date and time directly without timezone conversion
  if (typeof date === 'string' && date.includes('T')) {
    const [datePart, timePart] = date.split('T');
    const [year, month, day] = datePart.split('-');
    const [time] = timePart.split('.');
    const [hours, minutes] = time.split(':');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  
  // Fallback for Date objects
  const dateObj = new Date(date);
  
  // Format date in European style (dd/MM/yyyy)
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getUTCFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  
  // Format time using UTC methods
  const hours = dateObj.getUTCHours().toString().padStart(2, '0');
  const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
  const formattedTime = `${hours}:${minutes}`;
  
  return `${formattedDate} ${formattedTime}`;
};

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  status,
  isModified,
  userPermissions,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  // Determine if field is editable based on status and permissions
  const isQuantityField = ['QTE', 'qte_prepare', 'qte_controle', 'qte_liv'].includes(dataIndex);
  const isPriceOrDiscount = ['PV_TTC', 'REMISE'].includes(dataIndex);
  
  // If status is 'VALIDE' or 'TRANSFERE' or 'GRATUIT' or 'ZERO', nothing is editable unless user is manager
  const canEdit = currentCan('deliveryNotes', 'update') && ((status !== 'VALIDE' && status !== 'TRANSFERE' && status !== 'GRATUIT' && status !== 'ZERO') || userPermissions?.isManager);

  const isEditable = canEdit && (isPriceOrDiscount || (
    !status ? true :
    isQuantityField && (
      (status === "EN_PREPARATION" && dataIndex === "qte_prepare") ||
      (status === "PREPARE" && dataIndex === "qte_controle") ||
      (status === "VALIDE" && dataIndex === "qte_liv")
    )
  ));

  const toggleEdit = () => {
    if (!isEditable || record[dataIndex] === -1) return;
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();

      // If PV_TTC is changed, recalculate PV_HT and set REMISE to 0
      if (dataIndex === 'PV_TTC') {
        const newPvTtc = Number(values.PV_TTC);
        const tva = Number(record.TVA) || 0;
        const newPvHt = newPvTtc / (1 + tva / 100);

        handleSave({
          ...record,
          ...values,
          REMISE: 0,
          PV_HT: Math.round(newPvHt * 100) / 100
        });
      } else {
        handleSave({ ...record, ...values });
      }
    } catch (errInfo) {
      console.log('Échec de l\'enregistrement:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Form.Item
          style={{ margin: 0, flex: 1 }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} est requis.`,
            },
            {
              validator: async (_, value) => {
                if (isQuantityField) {
                  // Get stock from stocks array (filtered by CODE_ENT) first, then fallback
                  const stock = record?.article?.stocks?.[0];
                  const qteStk = stock?.qte_stk ?? record.article?.qte_stk ?? 0;
                  if (value > qteStk) {
                    throw new Error(`La quantité ne peut pas dépasser le stock disponible (${qteStk})`);
                  }
                }
              },
            },
          ]}
        >
          <InputNumber
            ref={inputRef}
            onPressEnter={save}
            min={0}
            step={isQuantityField ? 1 : (dataIndex === 'REMISE' ? 1 : 0.01)}
            precision={isQuantityField ? 0 : 2}
            max={dataIndex === 'REMISE' ? 100 : (isQuantityField ? (() => {
              // Get stock from stocks array (filtered by CODE_ENT) first, then fallback
              const stock = record?.article?.stocks?.[0];
              return stock?.qte_stk ?? record.article?.qte_stk ?? 0;
            })() : undefined)}
            disabled={!isEditable || record[dataIndex] === -1}
          />
        </Form.Item>
        <PermissionButton permissionModule="deliveryNotes" permissionAction="update"
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          onClick={save}
          style={{ width: '16px', height: '16px', padding: 0 }}
        />
      </div>
    ) : (
      <div 
        className="editable-cell-value-wrap" 
        style={{
          paddingRight: 24,
          cursor: isEditable && record[dataIndex] !== -1 ? "pointer" : "not-allowed",
          color: isModified ? '#f5222d' : 'inherit'
        }}
        onClick={toggleEdit}
      >
        {record[dataIndex] === -1 ? "N/A" : children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableField = ({ 
  value, 
  onChange, 
  options, 
  inputType = "input",
  status,
  userPermissions = { isManager: false },
  displayValue
}) => {
  const canEdit = currentCan('deliveryNotes', 'update') && ((status !== 'VALIDE' && status !== 'TRANSFERE' && status !== 'GRATUIT' && status !== 'ZERO') || userPermissions?.isManager);

  const handleChange = (e) => {
    if (!canEdit) return;
    const newValue = inputType === "select" ? e : e.target.value;
    onChange(newValue);
  };

  if (inputType === "select") {
    if (!canEdit) {
      // Ensure we render a string, not an object
      const displayText = displayValue || (typeof value === 'string' ? value : (value?.value || value?.label || String(value) || 'N/A'));
      return <span>{displayText}</span>;
    }
    
    // Handle both string and object options
    const getSelectValue = () => {
      if (!value) return undefined;
      if (typeof value === 'string') return value;
      // If value is an object, extract the value property
      return value?.value || value;
    };
    
    return (
      <Select
        value={getSelectValue()}
        onChange={handleChange}
        style={{ width: '200px' }}
        disabled={!canEdit}
        dropdownMatchSelectWidth={false}
        getPopupContainer={(trigger) => trigger.parentElement || document.body}
        allowClear={false}
      >
        {options?.map(option => {
          // Support both string options and object options {value, label}
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          return (
            <Option key={optionValue} value={optionValue}>
              {optionLabel}
            </Option>
          );
        })}
      </Select>
    );
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      style={{ width: '200px' }}
      disabled={!canEdit}
    />
  );
};

// Date Editor Component that preserves time when changing date
const DateTimeEditor = ({ value, onChange, disabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const formattedDateTime = value ? formatDateTime(value) : "Non fourni";
  
  // Function to handle date change but preserve time
  const handleDateChange = (date) => {
    if (!date || !value) {
      onChange(date ? date.toISOString() : null);
      setIsEditing(false);
      return;
    }
    
    // Preserve the time part from the original value
    const originalDate = new Date(value);
    const newDate = date.toDate();
    
    // Set the time from the original date to the new date
    newDate.setHours(originalDate.getHours());
    newDate.setMinutes(originalDate.getMinutes());
    newDate.setSeconds(originalDate.getSeconds());
    newDate.setMilliseconds(originalDate.getMilliseconds());
    
    onChange(newDate.toISOString());
    setIsEditing(false);
  };
  
  if (isEditing) {
    return (
      <Space>
        <DatePicker 
          defaultValue={value ? moment(value) : null} 
          onChange={handleDateChange}
          onBlur={() => setIsEditing(false)}
          autoFocus
          disabled={disabled}
          format="DD/MM/YYYY"
        />
        <PermissionButton
          type="text" 
          icon={<CloseOutlined />} 
          onClick={() => setIsEditing(false)}
          size="small"
        />
      </Space>
    );
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span>{formattedDateTime}</span>
      {!disabled && (
        <PermissionButton permissionModule="deliveryNotes" permissionAction="update"
          type="text" 
          icon={<CalendarOutlined />} 
          onClick={() => setIsEditing(true)}
          size="small"
          style={{ marginLeft: 4 }}
        />
      )}
    </div>
  );
};

const SingleBonliv = ({ bonlivId, onChangeView, userPermissions = { isManager: false } ,
  onChangesSaved,
  onChangesDetected  }) => {
  const {
    bonliv: initialBonliv,
    loading: loadingBonliv,
    error: errorBonliv,
    refetch: refetchBonliv,
  } = useFetchBonlivById(bonlivId);
  
  const {
    items: initialItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems,
  } = useFetchItemsInBonliv(bonlivId);
  
  const { validate, error: validateError } = useValidateBonliv(bonlivId);
  const { updateStatus, error: updateStatusError } = useUpdateBonlivStatus();
  const { handleSaveBonliv, error: saveError } = useSaveBonliv();

  // Get the list of users
  const { users, loading: loadingUsers } = useUsers();
  
  // Get the list of entrepots
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();
  const { modes: paymentModes } = usePaymentModes();
  const { modes: deliveryModes } = useDeliveryModes();

  const [localBonliv, setLocalBonliv] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const localItemsRef = useRef([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
  const { unvalidate } = useUnvalidateBonliv();

  // Retry mechanism state variables
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 3; // You can change this value or make it a prop
  const retryDelay = 1000; // Delay between retries in milliseconds
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshingClient, setRefreshingClient] = useState(false);


  const [isClosing, setIsClosing] = useState(false);
  const [syncDate, setSyncDate] = useState(null);
  const [controlUser, setControlUser] = useState(null);

 // Add this with your other state declarations at the component level
const isFirstMountRef = useRef(true);

// Then modify your useEffect
useEffect(() => {
  console.log('SingleBonliv mounted - entering detail view for bonliv:', bonlivId);
  bonlivLockService.enterDetailView();
  
  // Function to handle when user closes tab or navigates away from site
  const handleBeforeUnload = () => {
    console.log('Browser tab closing or navigating to different site - releasing bonliv lock');
    bonlivLockService.exitDetailView();
  };
  
  // Only listen for actual page unload events
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Clean up event listener when component unmounts
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    
    // Only exit detail view if this isn't the first mount/unmount cycle
    if (!isFirstMountRef.current) {
      console.log('SingleBonliv unmounting - exiting detail view for bonliv:', bonlivId);
      bonlivLockService.exitDetailView();
    } else {
      console.log('First mount/unmount cycle - not releasing bonliv lock');
      isFirstMountRef.current = false;
    }
  };
}, [bonlivId]); // Include bonlivId to reset if ID changes
  // Synchronize localBonliv with initialBonliv
  useEffect(() => {
    if (initialBonliv) {
      setLocalBonliv({ ...initialBonliv });
      // If initial bonliv has controler_par field, use that value
      if (initialBonliv.controler_par) {
        setControlUser(initialBonliv.controler_par);
      }
    }
  }, [initialBonliv]);

  // Synchronize localItems with initialItems with retry logic
  useEffect(() => {
    const synchronizeItems = async () => {
      if (initialItems && initialItems.length > 0) {
        setLocalItems(initialItems);
        localItemsRef.current = initialItems; // Update ref
        setAttempts(0); // Reset attempts on successful fetch
        console.log("Articles initiaux récupérés avec succès. Articles:", initialItems);
        
      } else if (!loadingItems && attempts < maxAttempts) {
        // If items are empty and max attempts not reached, retry
        console.log(`Nouvelle tentative de récupération des articles... Essai ${attempts + 1}`);
        try {
          await refetchItems();
          setAttempts(prev => prev + 1);
        } catch (error) {
          console.error(`Tentative de récupération ${attempts + 1} échouée:`, error);
          setAttempts(prev => prev + 1);
        }
      } else if (!loadingItems && attempts >= maxAttempts) {
        // Max attempts reached, set localItems to empty and optionally show a message
        setLocalItems([]);
        localItemsRef.current = [];

        console.log("Nombre maximal de tentatives atteint. Poursuite avec une liste d'articles vide.");
      }
    };

    synchronizeItems();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialItems,
    loadingItems,
    attempts,
    maxAttempts,
    refetchItems,
    retryDelay,
  ]);

  // Keep localItemsRef in sync with localItems - optimize by using reference equality
  useEffect(() => {
    if (localItemsRef.current !== localItems) {
      localItemsRef.current = localItems;
      
      // Lightweight logging for debugging
      console.log(`Items updated: ${localItems.length} items`);
    }
  }, [localItems]);



  const getStatusText = (status) => {
    switch (status) {
      case 'EN_PREPARATION':
        return 'En Préparation';
      case 'PREPARE':
        return 'Preparé';
      case 'VALIDE':
        return 'Validé';
      case 'TRANSFERE':
        return 'Transféré';
      case 'GROUPE':
        return 'Transféré Groupe';
      case 'GRATUIT':
        return 'Gratuit';
      case 'ZERO':
        return 'Zéro';
      default:
        return status;
    }
  };
  
  const getQuantityFieldForStatus = (status) => {
    switch (status) {
      case "EN_PREPARATION": return "qte_prepare";
      case "PREPARE": return "qte_controle";
      case "VALIDE":
      case "TRANSFERE": return "qte_liv";
      default: return "QTE";
    }
  };
  

// Optimize calculatedTotals with more specific dependencies and memoization guards
const calculatedTotals = useMemo(() => {
  // Skip calculation if we don't have required data
  if (!localBonliv || !localItems?.length) return null;

  console.log("Recalculating totals for items:", localItems.length);

  let totalHT = 0;
  let totalTTC = 0;

  const updatedItems = localItems.map((item) => {
    const quantityField = getQuantityFieldForStatus(localBonliv.status);
    const quantity = item[quantityField];

    if (quantity !== -1) {
      // Calculate TTC independently from PV_TTC
      const itemTotalTTC = Math.round((item.PV_TTC * (1 - item.REMISE / 100) * quantity) * 100) / 100;

      // Calculate HT independently from PV_HT (not derived from TTC)
      const itemTotalHT = Math.round((item.PV_HT * (1 - item.REMISE / 100) * quantity) * 100) / 100;

      totalHT += itemTotalHT;
      totalTTC += itemTotalTTC;

      return {
        ...item,
        TotalHT: itemTotalHT,
        TotalTTC: itemTotalTTC,
      };
    }
    return item;
  });

  // Round final totals to 2 decimal places
  totalHT = Math.round(totalHT * 100) / 100;
  totalTTC = Math.round(totalTTC * 100) / 100;

  return {
    updatedItems,
    totalHT,
    totalTTC
  };
}, [localItems, localBonliv?.status]);

// Add a manual trigger for recalculation
const triggerRecalculation = useCallback(() => {
  console.log("Manual recalculation triggered");
  
  // Force update of local items to trigger useMemo recalculation
  setLocalItems(prevItems => [...prevItems]);
}, []);

// Optimize this effect to minimize DOM updates and only update when needed
useEffect(() => {
  if (!calculatedTotals) return;
  
  const { totalHT, totalTTC } = calculatedTotals;
  
  // Only update bonliv if totals have changed significantly
  const hasTotalsChanged = 
    Math.abs(localBonliv.MNT_HT - totalHT) > 0.01 ||
    Math.abs(localBonliv.MNT_TTC - totalTTC) > 0.01;
    
  if (hasTotalsChanged) {
    setLocalBonliv(prev => ({
      ...prev,
      MNT_HT: totalHT,
      MNT_TTC: totalTTC,
      MNT_HTliv: totalHT,
      MNT_TTCliv: totalTTC,
    }));
  }
  
  // Don't update localItems unless necessary - this is critical for performance
  // Instead of always setting items, check if there are actual changes
  const { updatedItems } = calculatedTotals;
  const hasItemsChanged = !localItems.every((item, index) => {
    const updatedItem = updatedItems[index];
    return updatedItem && 
           item.TotalHT === updatedItem.TotalHT && 
           item.TotalTTC === updatedItem.TotalTTC;
  });
  
  if (hasItemsChanged) {
    setLocalItems(updatedItems);
  }
}, [calculatedTotals]);


const handleUnvalidateClick = async () => {
  try {
    Modal.confirm({
      title: 'Réinitialiser Bonliv',
      content: 'Êtes-vous sûr de vouloir Réinitialiser ce Bon de Livraison ? Cela le remettra à l\'état de préparation.',
      onOk: async () => {
        await unvalidate(bonlivId);
        message.success('Le Bonliv a été Réinitialisé avec succès.');
        await Promise.all([refetchBonliv(), refetchItems()]);
      },
    });
  } catch (error) {
    message.error(`Échec de la Réinitialisation du bonliv: ${error}`);
  }
};

// 1. First, define the handleClose function using useCallback
const handleClose = useCallback(() => {
  // Set closing state first for animation
  setIsClosing(true);
  
  // Short delay to allow animation to complete
  setTimeout(() => {
    // Then explicitly release the lock
    console.log('User manually closed bonliv detail view - releasing lock for:', bonlivId);
    bonlivLockService.exitDetailView();
    
    // Then change view
    onChangeView('list');
  }, 150); // Match the animation duration from containerVariants
}, [bonlivId, onChangeView]);

// 2. Now define updateStatusAndRefetch, which can safely reference handleClose
const updateStatusAndRefetch = useCallback(
  async (newStatus) => {
    try {
      await updateStatus(bonlivId, newStatus);
      if (newStatus === 'TRANSFERE') {
        notification.success({
          message: `Statut du Bonliv mis à jour: ${getStatusText(newStatus)}`,
          description: 'Une Facture a été créé',
          placement: 'bottomLeft',
        });
      } else {
        message.success(`Statut du Bonliv mis à jour: ${getStatusText(newStatus)}`);
      }
      
      // Refetch bonliv first
      await refetchBonliv();
      
      // If status change affects stock (VALIDATE or unvalidate), add small delay
      // to ensure stock updates in database are committed
      if (newStatus === 'VALIDE' || newStatus === 'PREPARE') {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Refetch items with updated stock data
      await refetchItems();

      // Reset local changes after refetching
      setHasLocalChanges(false);
      
      // If status is transferred, we need to go back to list view
      if (newStatus === 'TRANSFERE') {
        // First release the lock by exiting detail view
        bonlivLockService.exitDetailView();
        // Then handle close
        handleClose();
      }
    } catch (error) {
      message.error(`Échec de la mise à jour du statut: ${error}`);
    }
  },
  [bonlivId, updateStatus, refetchBonliv, refetchItems, handleClose]
);

  const handleFieldChange = (field, value) => {
    // Convert zonecontrole to string since database expects string type
    let processedValue = value;
    if (field === "zonecontrole") {
      processedValue = value ? String(value) : null;
    }
    
    setLocalBonliv((prevBonliv) => ({
      ...prevBonliv,
      [field]: processedValue,
    }));
    
    // If controler_par is changed, also update controlUser state
    if (field === "controler_par") {
      setControlUser(value);
    }
    
    setHasLocalChanges(true);
  };

  // Function to synchronize all date fields to the same date while preserving time
  const synchronizeDates = (targetDate) => {
    if (!targetDate) return;

    const dateFields = ['DATE_BL', 'date_preparation', 'date_controle', 'date_validation', 'date_transfert'];
    const fieldLabels = ['Création', 'Préparation', 'Contrôle', 'Validation', 'Transfert'];
    let updatedFields = [];
    
    dateFields.forEach((field, index) => {
      const currentDateTime = localBonliv[field];
      
      if (currentDateTime) {
        // Parse the current date-time
        const currentDate = new Date(currentDateTime);
        const newDate = new Date(targetDate);
        
        // Preserve the time (hours, minutes, seconds, milliseconds) from current date
        newDate.setHours(currentDate.getHours());
        newDate.setMinutes(currentDate.getMinutes());
        newDate.setSeconds(currentDate.getSeconds());
        newDate.setMilliseconds(currentDate.getMilliseconds());
        
        // Update the field with new date but same time
        handleFieldChange(field, newDate.toISOString());
        const hours = currentDate.getUTCHours().toString().padStart(2, '0');
        const minutes = currentDate.getUTCMinutes().toString().padStart(2, '0');
        updatedFields.push(`${fieldLabels[index]} (${hours}:${minutes})`);
      } else {
        // If no existing date, set to the target date with current time
        const newDate = new Date(targetDate);
        const now = new Date();
        newDate.setHours(now.getHours());
        newDate.setMinutes(now.getMinutes());
        newDate.setSeconds(now.getSeconds());
        newDate.setMilliseconds(now.getMilliseconds());
        
        handleFieldChange(field, newDate.toISOString());
        const hours = now.getUTCHours().toString().padStart(2, '0');
        const minutes = now.getUTCMinutes().toString().padStart(2, '0');
        updatedFields.push(`${fieldLabels[index]} (${hours}:${minutes})`);
      }
    });
    
    setSyncDate(targetDate);
    message.success(`Dates synchronisées: ${updatedFields.join(', ')}`);
  };

  // Function to show date synchronization modal
  const showDateSyncModal = () => {
    let selectedDate = null;
    
    Modal.confirm({
      title: 'Synchroniser toutes les dates',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>Sélectionnez une date pour synchroniser tous les champs de dates :</p>
          <DatePicker
            placeholder="Choisir une date"
            style={{ width: '100%' }}
            onChange={(date) => {
              selectedDate = date;
            }}
          />
          <div style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}>
            <p><strong>Champs qui seront synchronisés :</strong></p>
            <p>• Date Création<br/>
            • Date Préparation<br/>
            • Date Contrôle<br/>
            • Date Validation<br/>
            • Date Transfert</p>
          </div>
          <p style={{ fontSize: '0.85em', color: '#888', marginTop: '8px' }}>
            <em>Note: Les heures existantes seront conservées</em>
          </p>
        </div>
      ),
      okText: 'Synchroniser',
      cancelText: 'Annuler',
      width: 500,
      onOk: () => {
        if (selectedDate) {
          synchronizeDates(selectedDate.toDate());
        } else {
          message.warning('Veuillez sélectionner une date');
          return Promise.reject();
        }
      }
    });
  };

  const handleSave = (updatedRow) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.CODE_ART === updatedRow.CODE_ART ? updatedRow : item
      )
    );
    setHasLocalChanges(true);
    
  };

  const handleAddItemSuccess = (newItems) => {
    // Handle both single item and array of items
    const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];
    
    setLocalItems((prevItems) => {
      let updatedItems = [...prevItems];
      let addedCount = 0;
      let updatedCount = 0;

      itemsToAdd.forEach((newItem) => {
        // Ensure the item has the required structure and is not malformed
        if (!newItem || typeof newItem !== 'object' || !newItem.CODE_ART) {
          console.warn('Invalid item structure:', newItem);
          return;
        }

        // Remove any numeric keys that might have been added incorrectly
        const cleanItem = { ...newItem };
        Object.keys(cleanItem).forEach(key => {
          if (!isNaN(parseInt(key)) && isFinite(key)) {
            delete cleanItem[key];
          }
        });

        const existingItemIndex = updatedItems.findIndex(
          (item) => item.CODE_ART === cleanItem.CODE_ART
        );

        if (existingItemIndex !== -1) {
          updatedItems[existingItemIndex] = cleanItem;
          updatedCount++;
        } else {
          updatedItems.push(cleanItem);
          addedCount++;
        }
      });

      if (addedCount > 0 && updatedCount > 0) {
        message.success(`${addedCount} article(s) ajouté(s) et ${updatedCount} article(s) mis à jour.`);
      } else if (addedCount > 0) {
        message.success(`${addedCount} article(s) ajouté(s) avec succès.`);
      } else if (updatedCount > 0) {
        message.info(`${updatedCount} article(s) mis à jour.`);
      }

      return updatedItems;
    });
    setHasLocalChanges(true);
  };

  const handleDelete = (codeArt) => {
    setLocalItems((prevItems) =>
      prevItems.filter((item) => item.CODE_ART !== codeArt)
    );
    setHasLocalChanges(true);
    message.success(
      "Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder."
    );
  };

  const [comDrawerOpen, setComDrawerOpen] = useState(false);
  const [selectedCom, setSelectedCom] = useState('');
  const [commercialId, setCommercialId] = useState(null);
  const { Com: commercialData, loading: loadingCom } = useFetchComById(commercialId);
  
  // Add state for popover visibility
  const [printPopoverVisible, setPrintPopoverVisible] = useState(false);
  const [moreOptionsPopoverVisible, setMoreOptionsPopoverVisible] = useState(false);
  
  // Add these useEffect hooks after the existing ones
  useEffect(() => {
    if (localBonliv?.CODE_COM) {
      setCommercialId(localBonliv.CODE_COM);
    }
  }, [localBonliv?.CODE_COM]);
  
  useEffect(() => {
    if (commercialData) {
      setSelectedCom(`${commercialData.nom} (${commercialData.code_com})`);
    }
  }, [commercialData]);
  
  // Add the handler function
  const handleComSelect = (com) => {
    handleFieldChange("CODE_COM", com);
    setCommercialId(com);
    setComDrawerOpen(false);
  };
  



  const handleSaveChanges = async () => {
    setIsSummaryModalVisible(true);
  };


useEffect(() => {
      // Notify parent component when changes occur
      if (hasLocalChanges && onChangesDetected) {
        onChangesDetected();
      }
    }, [hasLocalChanges, onChangesDetected]);


  
  // Add a new function for the actual saving operation
  const confirmAndSaveChanges = async () => {
    try {
      setIsSaving(true);
      
      // Create a stripped-down version of localItems with only necessary fields
      const filteredItems = localItems.map(item => {
        // Only include fields explicitly needed by the backend
        const baseItemData = {
          REF_BL: item.REF_BL,
          CODE_ART: item.CODE_ART, // Keep this as it's needed by the backend
          QTE: item.QTE,
          GRATUIT: item.GRATUIT,
          PA_HT: item.PA_HT,
          PV_HT: item.PV_HT,
          PV_TTC: item.PV_TTC,
          REMISE: item.REMISE,
          TVA: item.TVA,
          TotalHT: item.TotalHT,
          TotalTTC: item.TotalTTC,
          qte_prepare: item.qte_prepare,
          qte_controle: item.qte_controle,
          depose: item.depose,
          prepare: item.prepare,
          controle: item.controle,
          qte_liv: item.qte_liv
        };
        
        // Check if this is a new item not in the original data
        const isNewItem = !initialItems.some(initialItem => 
          initialItem.CODE_ART === item.CODE_ART
        );
        
        // Only include ARTICLE for new items
        if (isNewItem) {
          baseItemData.ARTICLE = item.ARTICLE;
        }
        
        return baseItemData;
      });
      
      // Ensure bonliv only includes necessary fields
      const bonlivData = { 
        REF_BL: localBonliv.REF_BL,
        DATE_BL: localBonliv.DATE_BL,
        COMPTE: localBonliv.COMPTE,
        CODE_CLT: localBonliv.CODE_CLT,
        CLIENT: localBonliv.CLIENT,
        MNT_HT: localBonliv.MNT_HT,
        MNT_TTC: localBonliv.MNT_TTC,
        MNT_HTliv: localBonliv.MNT_HTliv,
        MNT_TTCliv: localBonliv.MNT_TTCliv,
        EN_FACTURE: localBonliv.EN_FACTURE,
        VALIDER: localBonliv.VALIDER,
        MODELIV: localBonliv.MODELIV,
        MODE_PAIE: localBonliv.MODE_PAIE,
        REMARQUE: localBonliv.REMARQUE,
        CODE_COM: localBonliv.CODE_COM,
        CODE_ADR: localBonliv.CODE_ADR,
        BASEHT: localBonliv.BASEHT,
        IMPRIMER: localBonliv.IMPRIMER,
        REMISEG: localBonliv.REMISEG,
        status: localBonliv.status,
        date_preparation: localBonliv.date_preparation,
        date_controle: localBonliv.date_controle,
        date_validation: localBonliv.date_validation,
        date_transfert: localBonliv.date_transfert,
        controler_par: localBonliv.controler_par || controlUser,
        prepare_par: localBonliv.prepare_par,
        zonecontrole: localBonliv.zonecontrole,
        CODE_ENT: localBonliv.CODE_ENT
      };
      
      const dataToSave = {
        bonliv: bonlivData,
        items: filteredItems,
      };
      
      await handleSaveBonliv(dataToSave);
      setHasLocalChanges(false);
      if (onChangesSaved) {
        onChangesSaved(); // Still call this for immediate response
      }
      message.success("Bonliv enregistré avec succès!");
      setIsSummaryModalVisible(false);
      setIsSaving(false);
      await Promise.all([refetchBonliv(), refetchItems()]);
    } catch (error) {
      console.error("Erreur d'enregistrement du bonliv:", error);
      message.error(`Échec de l'enregistrement du bonliv: ${error.message || error}`);
      setIsSaving(false);
    }
  };


  const handleValidateClick = async () => {
    try {
      await validate(bonlivId);
      message.success("Le Bonliv a été validé avec succès.");
      
      // Refetch bonliv data first
      await refetchBonliv();
      
      // Small delay to ensure stock updates in database are committed
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Refetch items with stock data - this will include updated stock values
      await refetchItems();
      
      // Force a re-render by updating local state if needed
      // The useEffect hooks should handle this automatically, but ensure it happens
    } catch (error) {
      message.error(`Échec de la validation du bonliv: ${error}`);
    }
  };

  const handleStatusChange = useCallback(
    async (newStatus) => {
      if (hasLocalChanges) {
        Modal.confirm({
          title: "Modifications non enregistrées",
          content:
            "Vous avez des modifications non enregistrées. Voulez-vous enregistrer avant de changer le statut?",
          onOk: async () => {
            await handleSaveChanges();
            await updateStatusAndRefetch(newStatus);
          },
          onCancel: () => {
            message.info(
              "Changement de statut annulé. Veuillez d'abord enregistrer vos modifications."
            );
          },
        });
      } else {
        await updateStatusAndRefetch(newStatus);
      }
    },
    [hasLocalChanges, handleSaveChanges, updateStatusAndRefetch]
  );


  const handlePrint = async () => {
    Modal.info({
      title: 'Options d\'impression',
      width: 400,
      content: (
        <div style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
              icon={<FilePdfOutlined />} 
              onClick={() => {
                Modal.destroyAll();
                const printRecuButton = document.querySelector('[data-recu-gratuit-print-button]');
                if (printRecuButton) {
                  printRecuButton.click();
                }
              }}
              style={{ width: '100%' }}
            >
              Imprimer Reçu Gratuit
            </PermissionButton>
          </Space>
        </div>
      ),
      okText: 'Annuler',
      okButtonProps: { style: { display: 'none' } },
      cancelText: 'Fermer',
      cancelButtonProps: { style: { display: 'block' } },
      closeIcon: <CloseOutlined />,
      closable: true,
      onCancel: () => Modal.destroyAll()
    });
  };

  const columns = [
    {
      title: "Code Art",
      dataIndex: "CODE_ART",
      key: "CODE_ART",
    },
    {
      title: "Article",
      dataIndex: "ARTICLE",
      key: "ARTICLE",
    },
    {
      title: "Quantité",
      dataIndex: "QTE",
      key: "QTE",
      editable: true,
      onCell: (record) => ({
        record,
        editable: true,
        dataIndex: "QTE",
        title: "Quantité",
        handleSave,
        status: localBonliv?.status,
      }),
    },
    {
      title: "Qté Préparée",
      dataIndex: "qte_prepare",
      key: "qte_prepare",
      editable: true,
      onCell: (record) => ({
        record,
        editable: true,
        dataIndex: "qte_prepare",
        title: "Qté Préparée",
        handleSave,
        status: localBonliv?.status,
      }),
      render: (text) => (text === -1 ? "N/A" : text),
    },
    {
      title: "Qté Contrôlée",
      dataIndex: "qte_controle",
      key: "qte_controle",
      editable: true,
      onCell: (record) => ({
        record,
        editable: true,
        dataIndex: "qte_controle",
        title: "Qté Contrôlée",
        handleSave,
        status: localBonliv?.status,
      }),
      render: (text) => (text === -1 ? "N/A" : text),
    },
    {
      title: "Qté Livrée",
      dataIndex: "qte_liv",
      key: "qte_liv",
      editable: true,
      onCell: (record) => ({
        record,
        editable: true,
        dataIndex: "qte_liv",
        title: "Qté Livrée",
        handleSave,
        status: localBonliv?.status,
      }),
      render: (text) => (text === -1 ? "N/A" : text),
    },
    {
      title: "Total TTC",
      dataIndex: "TotalTTC",
      key: "TotalTTC",
      render: (_, record) => {
        let quantity;
        switch (localBonliv?.status) {
          case "EN_PREPARATION":
            quantity = record.qte_prepare;
            break;
          case "PREPARE":
            quantity = record.qte_controle;
            break;
          case "VALIDE":
          case "TRANSFERE":
            quantity = record.qte_liv;
            break;
          default:
            quantity = record.QTE;
        }
        return quantity !== -1
    ? ` ${Math.round((quantity * (1 - record.REMISE / 100) * record.PV_TTC) * 100) / 100}`
    : "N/A";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer cet article?"
          onConfirm={() => handleDelete(record.CODE_ART)}
          okText="Oui"
          cancelText="Non"
        >
          <PermissionButton permissionModule="deliveryNotes" permissionAction="delete" icon={<DeleteOutlined />} danger>
            Supprimer
          </PermissionButton>
        </Popconfirm>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const editableColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        status: localBonliv?.status,
      }),
    };
  });


  const getDescriptionItems = () => [
    {
      key: 'clientInfo',
      label: 'Client',
      children: (
        <div>
          <Row gutter={16}>
            <Col span={14}>
              <Text strong>Client:</Text> {localBonliv.CLIENT} - {localBonliv.client?.VILLE}
            </Col>
            <Col span={8}>
              <Text strong>Compte:</Text> {localBonliv.COMPTE || "N/A"}
            <PermissionButton permissionModule="deliveryNotes" permissionAction="update"
              onClick={async () => {
                try {
                  if (!localBonliv) return;
                  // Only allow when editable or manager
                  const canEdit = currentCan('deliveryNotes', 'update') && ((localBonliv.status !== 'VALIDE' && localBonliv.status !== 'TRANSFERE' && localBonliv.status !== 'GRATUIT' && localBonliv.status !== 'ZERO') || userPermissions?.isManager);
                  if (!canEdit) {
                    message.warning("Actualisation non autorisée dans ce statut.");
                    return;
                  }
                  setRefreshingClient(true);
                  let latestClient = null;
                  if (localBonliv.client?.id) {
                    const res = await getClientById(localBonliv.client.id);
                    latestClient = res.data;
                  } else if (localBonliv.COMPTE) {
                    const result = await getAllClients({ compteSearch: localBonliv.COMPTE, pageSize: 1 });
                    latestClient = result.clients?.[0] || null;
                  }
                  if (!latestClient) {
                    message.warning("Client introuvable pour actualisation.");
                    return;
                  }
                  setLocalBonliv((prev) => ({
                    ...prev,
                    CODE_CLT: latestClient.CODE_CLT ?? prev.CODE_CLT,
                    CLIENT: latestClient.CLIENT ?? prev.CLIENT,
                    COMPTE: latestClient.COMPTE ?? prev.COMPTE,
                    MODELIV: latestClient.MODELIV ?? prev.MODELIV,
                    MODE_PAIE: latestClient.MODE_PAIE ?? prev.MODE_PAIE,
                    client: {
                      ...(prev?.client || {}),
                      ...latestClient,
                    },
                  }));
                  setHasLocalChanges(true);
                  message.success("Infos client actualisées.");
                } catch (e) {
                  message.error(`Échec d'actualisation du client: ${e.message || e}`);
                } finally {
                  setRefreshingClient(false);
                }
              }}
              type="link"
              icon={<ReloadOutlined />}
              size="small"
              loading={refreshingClient}
              style={{ padding: '0 4px', marginLeft: 8 }}
            >
              Actualiser
            </PermissionButton>
            </Col>
          </Row>
          <Row style={{ marginTop: '8px' }}>
            <Col span={24}>
              <Text strong>Conditions:</Text> {localBonliv.client?.conditions || "Aucune"}
            </Col>
          </Row>
        </div>
      ),
      span: 2,
    },
    {
      key: 'preparationDate',
      label: 'Date Préparation',
      children: (
        <DateTimeEditor
          value={localBonliv.date_preparation}
          onChange={(newDate) => handleFieldChange("date_preparation", newDate)}
          disabled={!((localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && userPermissions.isManager)}
        />
      ),
      span: 1,
    },
  
    // Row 1: Reference and Dates
    {
      key: 'refDevis',
      label: 'Réf. Devis',
      children: localBonliv.REF_DEV,
      span: 1,
    },
    {
      key: 'creationDate',
      label: 'Date Creation',
      children: (
        <DateTimeEditor
          value={localBonliv.DATE_BL}
          onChange={(newDate) => handleFieldChange("DATE_BL", newDate)}
          disabled={!((localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && userPermissions.isManager)}
        />
      ),
      span: 1,
    },
    
    {
      key: 'controleDate',
      label: 'Date Contrôle',
      children: (
        <DateTimeEditor
          value={localBonliv.date_controle}
          onChange={(newDate) => handleFieldChange("date_controle", newDate)}
          disabled={!((localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && userPermissions.isManager)}
        />
      ),
      span: 1,
    },
    // Row 2: Client and Commercial Info
   
    {
      key: 'amountTTC',
      label: 'Total(ttc)',
      children: ` ${localBonliv.MNT_TTC?.toFixed(2)}`,
      span: 1,
    },
    // Row 3: Financial Information
    {
      key: 'amountHT',
      label: 'Total(ht)',
      children: ` ${localBonliv.MNT_HT?.toFixed(2)}`,
      span: 1,
    },
   
    
    {
      key: 'validationDate',
      label: 'Date Validation',
      children: (
        <DateTimeEditor
          value={localBonliv.date_validation}
          onChange={(newDate) => handleFieldChange("date_validation", newDate)}
          disabled={!((localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && userPermissions.isManager)}
        />
      ),
      span: 1,
    },
  
    // Row 4: Additional Information
    {
      key: 'deliveryMode',
      label: 'M. livrais.',
      children: (
        <EditableField
          value={localBonliv.MODELIV}
          onChange={(value) => handleFieldChange("MODELIV", value)}
          options={deliveryModes}
          inputType="select"
          status={localBonliv.status}
          userPermissions={userPermissions}
        />
      ),
      span: 1,
    },
    {
      key: 'controler_par',
      label: 'Contrôlé par',
      children: (
        <Select
          style={{ width: '200px' }}
          placeholder="Sélectionner un contrôleur"
          value={localBonliv.controler_par}
          onChange={(value) => handleFieldChange("controler_par", value)}
          disabled={(localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && !userPermissions.isManager}
          loading={loadingUsers}
        >
          {users.map(user => (
            <Option key={user.UserID} value={user.UserID}>
              {user.Name || user.UserName}
            </Option>
          ))}
        </Select>
      ),
      span: 1,
    },
    {
      key: 'transferDate',
      label: 'Date Transfert',
      children: (
        <DateTimeEditor
          value={localBonliv.date_transfert}
          onChange={(newDate) => handleFieldChange("date_transfert", newDate)}
          disabled={!((localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && userPermissions.isManager)}
        />
      ),
      span: 1,
    },
  
    // Row 5: Status Dates
    
    {
      key: 'paymentMode',
      label: 'M. paiem.',
      children: (
        <EditableField
          value={localBonliv.MODE_PAIE}
          onChange={(value) => handleFieldChange("MODE_PAIE", value)}
          options={paymentModes}
          inputType="select"
          status={localBonliv.status}
          userPermissions={userPermissions}
        />
      ),
      span: 1,
    },
    {
      key: 'remarks',
      label: 'Remarque',
      children: (
        <EnhancedEditableField
          value={localBonliv.REMARQUE}
          onChange={(value) => handleFieldChange("REMARQUE", value)}
          isLongText={true}
          status={localBonliv.status}
          userPermissions={userPermissions}
        />
      ),
      span: 2,
    },
    {
      key: 'entrepot',
      label: 'Entrepôt',
      children: (
        <EditableField
          value={localBonliv.CODE_ENT}
          onChange={(value) => {
            handleFieldChange("CODE_ENT", value);
          }}
          options={entrepots?.map(e => ({ value: e.code_ent, label: `${e.code_ent} - ${e.name}` })) || []}
          inputType="select"
          userPermissions={userPermissions}
          status={localBonliv?.status}
          displayValue={localBonliv.entrepot ? `${localBonliv.entrepot.code_ent} - ${localBonliv.entrepot.name}` : (localBonliv.CODE_ENT || 'Non défini')}
        />
      ),
      span: 1,
    },
    {
      key: 'preparateur',
      label: 'Préparateur',
      children: (
        <Select
          style={{ width: '200px' }}
          placeholder="Sélectionner un préparateur"
          value={localBonliv.prepare_par}
          onChange={(value) => handleFieldChange("prepare_par", value)}
          disabled={(localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && !userPermissions.isManager}
          loading={loadingUsers}
        >
          {users.filter(user => hasPermission(user, 'preparation')).map(user => (
            <Option key={user.UserID} value={user.UserID}>
              {user.Name || user.UserName}
            </Option>
          ))}
        </Select>
      ),
      span: 1,
    },
    {
      key: 'zone_controleur',
      label: 'Zone Contrôleur',
      children: (
        <Select
          style={{ width: '200px' }}
          placeholder="Zone (1-5)"
          value={localBonliv.zonecontrole}
          onChange={(value) => handleFieldChange("zonecontrole", value)}
          disabled={(localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && !userPermissions.isManager}
        >
          {[1, 2, 3, 4, 5].map(zone => (
            <Option key={zone} value={zone}>
              Zone {zone}
            </Option>
          ))}
        </Select>
      ),
      span: 1,
    },
   
  ];


  const containerVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.97,
      transition: { 
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'EN_PREPARATION':
        return 'purple';
      case 'PREPARE':
        return 'cyan';
      case 'VALIDE':
        return 'green';
      case 'TRANSFERE':
        return 'blue';
      case 'GROUPE':
        return 'orange';
      case 'GRATUIT':
        return 'gold';
      case 'ZERO':
        return 'red';
      default:
        return 'default';
    }
  };
  
  const getStatusButtons = (status) => {
    const buttonStyle = { minWidth: '120px' };

    switch (status) {
      case 'EN_PREPARATION':
        return (
          <Button
            type="primary"
            onClick={() => handleStatusChange('PREPARE')}
            disabled={hasLocalChanges}
            style={buttonStyle}
          >
            Preparé
          </Button>
        );

      case 'PREPARE':
        return (
          <Space>
            <PermissionButton permissionModule="deliveryNotes" permissionAction="validate"
              type="primary"
              onClick={() => handleStatusChange('VALIDE')}
              disabled={hasLocalChanges}
              style={buttonStyle}
            >
              Valider BL
            </PermissionButton>
            <PermissionButton permissionModule="deliveryNotes" permissionAction="unvalidate"
              onClick={() => handleUnvalidateClick()}
              disabled={hasLocalChanges}
              style={buttonStyle}
            >
              Réinitialiser
            </PermissionButton>
          </Space>
        );

      case 'VALIDE':
        const moreMenuContent = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(!localBonliv.client?.groupe && (localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') ) && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
                onClick={() => {
                  handlePrint();
                  setMoreOptionsPopoverVisible(false);
                }}
                icon={<PrinterOutlined />}
                style={{ width: '100%' }}
              >
                Imprimer
              </PermissionButton>
            )}
            <PermissionButton permissionModule="deliveryNotes" permissionAction="free"
              type="default"
              onClick={() => {
                handleStatusChange('GRATUIT');
                setMoreOptionsPopoverVisible(false);
              }}
              disabled={hasLocalChanges}
              style={{ backgroundColor: '#fadb14', borderColor: '#fadb14', color: '#fff', width: '100%' }}
            >
              Gratuit
            </PermissionButton>
            <PermissionButton permissionModule="deliveryNotes" permissionAction="zero"
              type="default"
              onClick={() => {
                handleStatusChange('ZERO');
                setMoreOptionsPopoverVisible(false);
              }}
              disabled={hasLocalChanges}
              style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff', width: '100%' }}
            >
              Zéro
            </PermissionButton>
          </div>
        );

        return (
          <Space>
            {(userPermissions.isManager || localBonliv.status === 'VALIDE') && !localBonliv.client?.groupe && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="transfer"
                type="primary"
                onClick={() => handleStatusChange('TRANSFERE')}
                disabled={hasLocalChanges}
                style={buttonStyle}
              >
                Transférer {'>'} Module Fac
              </PermissionButton>
            )}
            {(userPermissions.isManager || localBonliv.status === 'VALIDE') && localBonliv.client?.groupe && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="group"
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleTransferToGroupe}
                disabled={hasLocalChanges || transferLoading}
                loading={transferLoading}
                style={buttonStyle}
              >
                Transférer {'>'} Groupe
              </PermissionButton>
            )}
            
            <PermissionButton permissionModule="deliveryNotes" permissionAction="unvalidate"
              onClick={() => handleUnvalidateClick()}
              disabled={hasLocalChanges}
              style={buttonStyle}
            >
              Réinitialiser
            </PermissionButton>
            <Popover
              content={moreMenuContent}
              title="Options supplémentaires"
              trigger="click"
              placement="bottomRight"
              open={moreOptionsPopoverVisible}
              onOpenChange={setMoreOptionsPopoverVisible}
            >
              <Button
                icon={<MoreOutlined />}
                disabled={hasLocalChanges}
                style={buttonStyle}
              >
                Plus
              </Button>
            </Popover>
          </Space>
        );

      case 'TRANSFERE':
      case 'GROUPE':
      case 'GRATUIT':
      case 'ZERO':
        const printMenuContent = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {!localBonliv.client?.groupe && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
                onClick={() => {
                  handlePrint();
                  setPrintPopoverVisible(false);
                }}
                icon={<PrinterOutlined />}
                style={{ width: '100%' }}
              >
                Imprimer
              </PermissionButton>
            )}
            {localBonliv.client?.groupe && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
                onClick={() => {
                  const printGroupeButton = document.querySelector('[data-print-groupe-button]');
                  if (printGroupeButton) {
                    printGroupeButton.click();
                  }
                  setPrintPopoverVisible(false);
                }}
                icon={<PrinterOutlined />}
                style={{ width: '100%' }}
              >
                Imprimer Groupe
              </PermissionButton>
            )}
          </div>
        );

        return (
          <Space>
            {(userPermissions.isManager && localBonliv.status === 'TRANSFERE') && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="transfer"
                type="primary"
                onClick={() => handleStatusChange('TRANSFERE')}
                disabled={hasLocalChanges}
                style={buttonStyle}
              >
                Transférer
              </PermissionButton>
            )}
            {(userPermissions.isManager && localBonliv.status === 'GROUPE') && (
              <PermissionButton permissionModule="deliveryNotes" permissionAction="group"
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleTransferToGroupe}
                disabled={hasLocalChanges || transferLoading}
                loading={transferLoading}
                style={buttonStyle}
              >
                Transférer {'>'} Groupe
              </PermissionButton>
            )}
            <PermissionButton permissionModule="deliveryNotes" permissionAction="unvalidate"
              onClick={() => handleUnvalidateClick()}
              disabled={hasLocalChanges}
              style={buttonStyle}
            >
              Réinitialiser
            </PermissionButton>
            <Popover
              content={printMenuContent}
              title="Options d'impression"
              trigger="click"
              placement="bottomRight"
              open={printPopoverVisible}
              onOpenChange={setPrintPopoverVisible}
            >
              <Button
                icon={<MoreOutlined />}
                disabled={false}
                style={buttonStyle}
              >
                Plus
              </Button>
            </Popover>
          </Space>
        );

      default:
        return null;
    }
  };

  const memoizedItems = useMemo(() => localItems, [localItems]);

  const { transfer, error: transferError, loading: transferLoading } = useTransferBonlivToGroupe();

  const handleTransferToGroupe = useCallback(async () => {
    try {
      Modal.confirm({
        title: 'Transférer vers Groupe',
        content: 'Êtes-vous sûr de vouloir transférer ce bon de livraison vers le système Groupe? Un fichier Excel sera généré et envoyé par FTP.',
        onOk: async () => {
          try {
            await transfer(bonlivId);
            notification.success({
              message: `Transfert Groupe réussi`,
              description: 'Le Bonliv a été transféré avec succès vers le système Groupe.',
              placement: 'bottomLeft',
            });
            await Promise.all([refetchBonliv(), refetchItems()]);
            setHasLocalChanges(false);
          } catch (error) {
            message.error(`Échec du transfert vers Groupe: ${error.response?.data?.message || error.message}`);
          }
        },
        okText: 'Confirmer',
        cancelText: 'Annuler',
      });
    } catch (error) {
      message.error(`Erreur: ${error.message}`);
    }
  }, [bonlivId, transfer, refetchBonliv, refetchItems]);

  if (loadingBonliv || (loadingItems && attempts < maxAttempts)) return <Spin tip="Chargement..." />;
  if (errorBonliv || errorItems)
    return (
      <Alert
        message="Erreur"
        description={errorBonliv || errorItems}
        type="error"
        showIcon
      />
    );

  if (!localBonliv) return <Alert message="Aucun bonliv trouvé" type="info" showIcon />;




  return (
    <motion.div
    initial="hidden"
    animate={isClosing ? "exit" : "visible"}
    variants={containerVariants}
    style={{
      transformOrigin: 'center center',
      willChange: 'transform, opacity'
    }}
  >
      <Card
        title={
          <>
            Bon de livraison - {localBonliv.REF_BL}
            <Tag color={getStatusColor(localBonliv.status)} style={{ marginLeft: "8px" }}>
  {getStatusText(localBonliv.status)}
</Tag>


{localBonliv.date_validation ? (
    <span style={{ color: "#888888", fontSize: "0.85em", marginLeft: "4px" }}>
      {formatDate(localBonliv.date_validation)}
    </span>
  ) : ''}
          </>
        }
        bordered={false}
        extra={
          <Space>

{hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler toutes les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications? Cela rétablira toutes les modifications à l\'état sauvegardé précédemment.',
                    okText: 'Oui, tout annuler',
                    cancelText: 'Non, garder les modifications',
                    onOk: () => {
                      setLocalBonliv({ ...initialBonliv });
                      setLocalItems([...initialItems]);
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler les modif.
              </PermissionButton>
            )}
            {getStatusButtons(localBonliv.status)}
            

            
            <PermissionButton permissionModule="deliveryNotes" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
            >
              Enregistrer Modif.
            </PermissionButton>
            <div style={{ display: 'none' }}>
              <PrintRecuGratuit bonliv={localBonliv} items={localItems} />
              <PrintGroupe bonliv={localBonliv} items={localItems} />
            </div>
 <Button 
  type="default"
  onClick={handleClose}
  style={{ 
    backgroundColor: 'white',
    color: '#EE2625',
    border: '1px solid #EE2625',
  }}
>
  SORTIR
</Button>

          </Space>
        }
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>

      <Card 
        title="Articles dans le Bonliv" 
        style={{ marginTop: 20 }} 
        extra={
          <Space>
            <Button
              icon={<CalendarOutlined />}
              onClick={showDateSyncModal}
              type="default"
              title="Synchroniser toutes les dates"
              disabled={
                (localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && 
                !userPermissions.isManager
              }
            />
            <PermissionButton permissionModule="deliveryNotes" permissionAction="update"
  onClick={() => setIsAddItemDrawerVisible(true)}
  type="primary"
  disabled={
    (localBonliv.status === 'VALIDE' || localBonliv.status === 'TRANSFERE' || localBonliv.status === 'GRATUIT' || localBonliv.status === 'ZERO') && 
    !userPermissions.isManager
  }
>
              Ajouter un article
            </PermissionButton>
          </Space>
        }
      >
       <NestedBonlivTable
  items={memoizedItems}
  handleSave={handleSave}
  handleDelete={handleDelete}
  status={localBonliv.status}
  userPermissions={userPermissions}
  onValueChange={triggerRecalculation}
/>
      </Card>

      <Drawer
        title="Ajouter un article au Bonliv"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <BonlivAddItemForm
          refBonliv={localBonliv.REF_BL}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
          bonlivStatus={localBonliv.status}
        />
      </Drawer>

      <Drawer
  title="Sélectionner un commercial"
  width={720}
  onClose={() => setComDrawerOpen(false)}
  open={comDrawerOpen}
>
  <ComList onSelectCom={handleComSelect} />
</Drawer>


    <ChangesSummaryModal
      visible={isSummaryModalVisible}
      onClose={() => setIsSummaryModalVisible(false)}
      onConfirm={confirmAndSaveChanges}
      originalBonliv={initialBonliv}
      modifiedBonliv={localBonliv}
      originalItems={initialItems}
      modifiedItems={localItems}
      loading={isSaving}
    />
  
  </motion.div>
  );
};

export default SingleBonliv;
